/**
 * Bandeaux d'estimation par agrégateur — repris de la version du client.
 *
 * Aucun appel réseau : le composant se contente d'afficher le résumé qu'on lui
 * passe. C'est la page qui adapte notre `/revenue-summary` à la forme qu'il
 * attend, et cette adaptation est isolée là-bas, jamais ici : le composant reste
 * comparable au sien, mot pour mot.
 */
import { TrendingUp, AlertCircle, Sparkles } from 'lucide-react';
import MentionEstimation from '@/shared/components/MentionEstimation';

const fmtEUR = (v, decimals = 2) =>
  v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} €`;

function KPI({ label, value, accent, tid, tone = 'cyan' }) {
  const map = {
    cyan: 'text-cyan-700 border-cyan-100/60',
    violet: 'text-violet-700 border-violet-100/60',
  };
  return (
    <div className={`rounded-md bg-white border px-3 py-2 ${map[tone]}`} data-testid={tid}>
      <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">{label}</p>
      <p className={`text-lg font-semibold mt-0.5 leading-tight ${accent ? map[tone].split(' ')[0] : 'text-neutral-900'}`}>{value}</p>
    </div>
  );
}

export function SymphonicsRevenueWidget({ summary, showAlerts = true }) {
  if (!summary) return null;
  const byType = summary.by_type || {};
  const rows = ['PAC', 'Ballon', 'Batterie', 'PV'].map((t) => ({ type: t, ...(byType[t] || byType[t === 'Ballon' ? 'ECS' : ''] || {}) }));
  const hasEnAttente = Object.values(byType).some((n) => (n.en_attente || 0) > 0);
  const partOp = Number(summary.config?.part_operateur ?? 1);
  return (
    <div className="rounded-lg border border-cyan-100 bg-gradient-to-br from-cyan-50/40 to-white p-5 space-y-4" data-testid="symphonics-revenue-widget">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-cyan-100 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-cyan-700" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-neutral-900">Estimation Symphonics</h2>
            <p className="text-[11px] text-neutral-500">
              {summary.total_actifs_symphonics ?? 0} actifs sous mandat · {summary.total_clients_symphonics ?? 0} clients ·
              <span className="text-emerald-700 font-medium ml-1" data-testid="symph-widget-part-op">
                Part opérateur {Math.round(partOp * 100)} %
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Estimation mensuelle" value={fmtEUR(summary.total_mensuel)} accent tid="symph-kpi-mensuel" tone="cyan" />
        <KPI label="Estimation annuelle" value={fmtEUR(summary.total_annuel)} tid="symph-kpi-annuel" tone="cyan" />
        <KPI label="MECAPA /an estimé" value={fmtEUR(summary.total_mecapa_an)} tid="symph-kpi-mecapa" tone="cyan" />
        <KPI label="Énergie /an estimée" value={fmtEUR(summary.total_energie_an)} tid="symph-kpi-energie" tone="cyan" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {rows.map((r) => (
          <div key={r.type} className="rounded-md border border-cyan-100/60 bg-white px-3 py-2 flex items-center justify-between" data-testid={`symph-widget-type-${r.type}`}>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-neutral-700">{r.type === 'Ballon' ? 'Ballon ECS' : r.type}</p>
              <p className="text-[10px] text-neutral-400">{r.count || 0} actif{(r.count || 0) > 1 ? 's' : ''}</p>
            </div>
            {r.count > 0 && r.calcules === 0 && r.en_attente > 0 ? (
              <span className="text-[10px] text-amber-600 font-medium">En attente</span>
            ) : (
              <span className="text-[12px] font-semibold text-cyan-700">
                {fmtEUR(r.mensuel || 0, 0)}
                <span className="text-[9px] text-neutral-400 font-normal">/m</span>
              </span>
            )}
          </div>
        ))}
      </div>
      {showAlerts && hasEnAttente && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50/70 rounded-md px-2.5 py-1.5">
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>Certains actifs sont en attente de paramétrage Symphonics (coefficient dispo Ballon).</span>
        </div>
      )}
      <MentionEstimation source="symphonics" bloc="widget-symphonics" />
    </div>
  );
}

export function SurvoltageRevenueWidget({ summary }) {
  if (!summary) return null;
  const totals = summary.totals || {};
  const byProfile = summary.by_profile || {};
  const byFamily = summary.by_family || {};
  const partOp = Number(summary.config?.part_operateur ?? 1);
  const topProfiles = Object.entries(byProfile)
    .filter(([, n]) => (n.count || 0) > 0)
    .sort((a, b) => (b[1].total_median || 0) - (a[1].total_median || 0))
    .slice(0, 4);
  return (
    <div className="rounded-lg border border-violet-100 bg-gradient-to-br from-violet-50/40 to-white p-5 space-y-4" data-testid="survoltage-revenue-widget">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-violet-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-violet-700" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-neutral-900">Estimation Survoltage</h2>
            <p className="text-[11px] text-neutral-500">
              {totals.clients ?? 0} clients répartis en {topProfiles.length} profil{topProfiles.length > 1 ? 's' : ''} actif{topProfiles.length > 1 ? 's' : ''} ·
              <span className="text-emerald-700 font-medium ml-1" data-testid="surv-widget-part-op">
                Part opérateur {Math.round(partOp * 100)} %
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Médian mensuel estimé" value={fmtEUR(totals.monthly_median, 0)} accent tid="surv-kpi-mensuel" tone="violet" />
        <KPI label="Médian annuel estimé" value={fmtEUR(totals.annual_median, 0)} tid="surv-kpi-annuel" tone="violet" />
        <KPI label="Fourchette basse estimée" value={fmtEUR(totals.annual_low, 0)} tid="surv-kpi-bas" tone="violet" />
        <KPI label="Fourchette haute estimée" value={fmtEUR(totals.annual_high, 0)} tid="surv-kpi-haut" tone="violet" />
      </div>
      {topProfiles.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10.5px] uppercase tracking-wide text-neutral-500 font-semibold">Top profils</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {topProfiles.map(([pkey, n]) => (
              <div key={pkey} className="rounded-md border border-violet-100/60 bg-white px-3 py-2 flex items-center justify-between" data-testid={`surv-widget-profile-${pkey}`}>
                <div className="min-w-0">
                  <p className="text-[11.5px] font-medium text-neutral-700 truncate">{n.label}</p>
                  <p className="text-[10px] text-neutral-400">{n.count} client{n.count > 1 ? 's' : ''}</p>
                </div>
                <span className="text-[12px] font-semibold text-violet-700">
                  {fmtEUR(n.total_median, 0)}
                  <span className="text-[9px] text-neutral-400 font-normal">/an</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="rounded-md bg-violet-50/60 px-2.5 py-1.5">
          <p className="text-neutral-500 text-[10px]">Capacitaire estimé</p>
          <p className="font-semibold text-neutral-800">{fmtEUR(byFamily.capacitaire, 0)}</p>
        </div>
        <div className="rounded-md bg-violet-50/60 px-2.5 py-1.5">
          <p className="text-neutral-500 text-[10px]">Énergie estimée</p>
          <p className="font-semibold text-neutral-800">{fmtEUR(byFamily.energie, 0)}</p>
        </div>
        <div className="rounded-md bg-violet-50/60 px-2.5 py-1.5">
          <p className="text-neutral-500 text-[10px]">Équilibrage estimé</p>
          <p className="font-semibold text-neutral-800">{fmtEUR(byFamily.equilibrage, 0)}</p>
        </div>
      </div>
      <MentionEstimation source="survoltage" bloc="widget-survoltage" />
    </div>
  );
}
